import { GregorioClefInfo } from '../structures/GregorioClef';
import { GregorioGlyph } from '../structures/GregorioGlyph';
import { GabcDetermination } from '../structures/GabcDetermination';
import { GregorioGlyphType } from '../enums/GregorioGlyphType';
import { GregorioLiquescentia } from '../enums/GregorioLiquescentia';
import { GregorioNoteElement } from '../structures/GregorioNoteElement';
import { GregorioScore } from '../structures/GregorioScore';
import { GregorioShape } from '../enums/GregorioShape';
import { GregorioSign } from '../enums/GregorioSign';
import { GregorioMiscElementInfo } from '../structures/GregorioSyllable';
import { GregorioType } from '../enums/GregorioType';
import { add_note_to_a_glyph, is_punctum_inclinatum } from './add_note_to_a_glyph';
import { close_fusion_glyph } from './close_fusion_glyph';
import { close_normal_glyph } from './close_normal_glyph';
import { gregorio_add_unpitched_element_as_glyph, is_tail_liquescentia } from './other';


function close_glyph(
  glyphs: GregorioGlyph[],
  notes: GregorioNoteElement[],
  glyph_type: GregorioGlyphType,
  first_note_i: number,
  liquescentia: GregorioLiquescentia,
  current_note_i: number,
) {
  let result_glyph_type = glyph_type;

  /* special case for fusion when rarer shapes don't exist */
  if (result_glyph_type === GregorioGlyphType.TORCULUS) {
    switch (notes[first_note_i].shape) {
      case GregorioShape.ORISCUS_UNDETERMINED:
      case GregorioShape.ORISCUS_ASCENDENS:
      case GregorioShape.ORISCUS_DESCENDENS:
      case GregorioShape.ORISCUS_SCAPUS_UNDETERMINED:
      case GregorioShape.ORISCUS_SCAPUS_ASCENDENS:
      case GregorioShape.ORISCUS_SCAPUS_DESCENDENS:
        result_glyph_type = GregorioGlyphType.FUSED;
        break;
      default:
        break;
    }
  }

  if (result_glyph_type == GregorioGlyphType.FUSED) {
    close_fusion_glyph(
      glyphs,
      notes,
      first_note_i,
      liquescentia);
  } else {
    close_normal_glyph(
      glyphs,
      notes,
      glyph_type,
      first_note_i,
      liquescentia,
      current_note_i);
  }
}

export function gabc_det_glyphs_from_notes(
  notes: GregorioNoteElement[],
  score: GregorioScore,
): GregorioGlyph[] {
  /* the first note of the current glyph, to be able to close it well:
    * later we will cut the link (next_notes and previous_note) between
    * this note and the previous one */
  const glyphs: GregorioGlyph[] = [];

  let current_key = 0;

  let current_glyph_first_note_index = 0;

  /* the last glyph we have totally determined. It is automatically
   * updated by close_glyph(). */
  let last_glyph: GregorioGlyph | undefined = undefined;

  /* type of the glyph we are currently determining */
  let current_glyph_type: GregorioGlyphType = GregorioGlyphType.UNDETERMINED;
  let next_glyph_type: GregorioGlyphType = GregorioGlyphType.UNDETERMINED;
  let last_pitch = 0;

  /* determination of end of glyphs, see comments on
   * add_note_to_a_glyph */
  let end_of_glyph: GabcDetermination = GabcDetermination.NO_END;

  /* a char representing the liquescentia of the current glyph */
  let glyph_liquescentia: GregorioLiquescentia = GregorioLiquescentia.NO_LIQUESCENTIA;

  for (let current_note_index = 0; current_note_index < notes.length; current_note_index++) {
    const current_note = notes[current_note_index];
    const next_note = notes.length === current_note_index + 1 ? undefined : notes[current_note_index + 1];
    const previous_note = current_note_index === 0 ? undefined : notes[current_note_index - 1];

    if (current_note.type != GregorioType.NOTE) {
      const type = current_note.type;
      let pitch = 0;
      let force = false;
      let sign = GregorioSign.NO_SIGN;

      if (current_glyph_type != GregorioGlyphType.UNDETERMINED) {
        close_glyph(
          glyphs,
          notes,
          next_glyph_type,
          current_glyph_first_note_index,
          glyph_liquescentia,
          current_note_index - 1);
        current_glyph_first_note_index = current_note_index;
        current_glyph_type = GregorioGlyphType.UNDETERMINED;
        glyph_liquescentia = GregorioLiquescentia.NO_LIQUESCENTIA;
      }

      switch (current_note.type) {
        case GregorioType.CLEF:
          pitch = current_note.pitch;
          current_key = current_note.clef.gregorio_calculate_new_key();
          break;

        case GregorioType.MANUAL_CUSTOS:
          pitch = current_note.pitch;
          current_note.type = GregorioType.CUSTOS;
          force = true;
          break;

        case GregorioType.BAR:
          /* we calculate the signs of the bars */
          if (current_note.signs == GregorioSign.V_EPISEMA) {
            sign = GregorioSign.V_EPISEMA;
          } else {
            sign = current_note.special_sign;
          }
          if (current_note.h_episema_above) {
            if (sign == GregorioSign.V_EPISEMA) {
              sign = GregorioSign.V_EPISEMA_BAR_H_EPISEMA;
            } else {
              sign = GregorioSign.BAR_H_EPISEMA;
            }
          }
          break;

        default:
          /* do nothing */
          break;
      }

      if (pitch === 0) {
        gregorio_add_unpitched_element_as_glyph(
          glyphs,
          type,
          current_note.other,
          sign);
      } else if (type === GregorioType.CLEF) {
        gregorio_add_clef_as_glyph(
          glyphs,
          current_note.clef,
        );
      } else {
        gregorio_add_pitched_element_as_glyph(
          glyphs,
          type,
          pitch,
          force,
        );
      }
      current_glyph_first_note_index = current_note_index + 1;
      // current_note.texverb = 0;
      // gregorio_free_one_note(notes, current_note);
      last_pitch = 0;
      continue;
    }

    /* first we do what must be done with liquescentia */
    const current_liquescentia =
      current_note.liquescentia & (GregorioLiquescentia.INITIO_DEBILIS | GregorioLiquescentia.FUSED);

    if (current_liquescentia) {
      /* initio debilis or fused */
      if (current_glyph_type !== GregorioGlyphType.UNDETERMINED) {
        /* if it is not the first glyph */
        close_glyph(
          glyphs,
          notes,
          current_glyph_type,
          current_glyph_first_note_index,
          current_liquescentia,
          current_note_index - 1);
        current_glyph_first_note_index = current_note_index;
        current_glyph_type = GregorioGlyphType.UNDETERMINED;
      }

      glyph_liquescentia = current_liquescentia;
    }

    const res = add_note_to_a_glyph(
      current_glyph_type,
      current_note.pitch,
      last_pitch,
      current_note.shape,
      current_note.liquescentia,
      notes[current_glyph_first_note_index]);

    next_glyph_type = res.next_glyph_type;
    end_of_glyph = res.end_of_glyph;

    /* patch to have good shapes in the special cases of pes quadratum and
     * pes quilisma quadratum. */
    switch (current_note.shape) {
      case GregorioShape.QUADRATUM:
        current_note.shape = (GregorioShape.PUNCTUM);
        break;

      case GregorioShape.QUILISMA_QUADRATUM:
        current_note.shape = (GregorioShape.QUILISMA);
        break;

      default:
        /* do nothing */
        break;
    }

    /* see comments on add_note_to_a_glyph for the meaning of
     * end_of_glyph */
    switch (end_of_glyph) {
      case GabcDetermination.NO_END:
        current_glyph_type = next_glyph_type;
        /*
         * we deal with liquescentia
         */
        if (is_tail_liquescentia(current_note.liquescentia)) {
          /* special cases of oriscus descendens, treated like normal
           * oriscus in some cases. */
          // if (score.legacy_oriscus_orientation) {
          //   if (current_note.shape == GregorioShape.ORISCUS_DESCENDENS
          //     && next_note
          //     && next_note.type == GregorioType.NOTE
          //     && next_note.pitch < current_note.pitch) {
          //     last_pitch = current_note.pitch;
          //     current_note.shape = (GregorioShape.ORISCUS_ASCENDENS);
          //     current_note.liquescentia =
          //       GregorioLiquescentia.NO_LIQUESCENTIA;
          //     // current_note = next_note;
          //     continue;
          //   }
          // }
          /* special cases of the punctum inclinatum deminutus and
           * auctus */
          if (is_punctum_inclinatum(current_note.shape, false)) {
            if (current_note.liquescentia == GregorioLiquescentia.DEMINUTUS) {
              current_note.shape = (GregorioShape.PUNCTUM_INCLINATUM_DEMINUTUS);
            }
            if (current_note.liquescentia == GregorioLiquescentia.AUCTUS_DESCENDENS ||
              current_note.liquescentia == GregorioLiquescentia.AUCTUS_ASCENDENS) {
              current_note.shape = (GregorioShape.PUNCTUM_INCLINATUM_AUCTUS);
            }

            if (next_note
              && next_note.type == GregorioType.NOTE
              && is_punctum_inclinatum(next_note.shape, false)
              && next_note.liquescentia === GregorioLiquescentia.DEMINUTUS) {
              last_pitch = current_note.pitch;
              // current_note = next_note;
              continue;
            }
          }
          
          glyph_liquescentia |= current_note.liquescentia;
          
          close_glyph(
            glyphs,
            notes,
            current_glyph_type,
            current_glyph_first_note_index,
            glyph_liquescentia,
            current_note_index);
          current_glyph_first_note_index = current_note_index;
          current_glyph_type = GregorioGlyphType.UNDETERMINED;
          glyph_liquescentia = GregorioLiquescentia.NO_LIQUESCENTIA;
        }
        break;
      case GabcDetermination.END_OF_PREVIOUS:
        if (previous_note) {
          /* we don't want to close previous glyph twice */
          close_glyph(
            glyphs,
            notes,
            current_glyph_type,
            current_glyph_first_note_index, glyph_liquescentia,
            current_note_index - 1);
          current_glyph_first_note_index = current_note_index;
        }
        current_glyph_type = next_glyph_type;
        glyph_liquescentia = GregorioLiquescentia.NO_LIQUESCENTIA;
        last_pitch = 0;
        /* we deal with liquescentia */
        if (is_tail_liquescentia(current_note.liquescentia))
                   /* not an initio debilis, because we considered it in the first
                    * part... */ {
          /* special cases of the punctum inclinatum deminutus and
           * auctus */
          if (is_punctum_inclinatum(current_note.shape, false)) {
            if (current_note.liquescentia == GregorioLiquescentia.DEMINUTUS) {
              current_note.shape = (GregorioShape.PUNCTUM_INCLINATUM_DEMINUTUS);
            }
            if (current_note.liquescentia === GregorioLiquescentia.AUCTUS_DESCENDENS ||
              current_note.liquescentia === GregorioLiquescentia.AUCTUS_ASCENDENS) {
              current_note.shape = (GregorioShape.PUNCTUM_INCLINATUM_AUCTUS);
            }
            if (next_note && next_note.type === GregorioType.NOTE &&
              is_punctum_inclinatum(next_note.shape, false) &&
              next_note.liquescentia === GregorioLiquescentia.DEMINUTUS) {
              // current_note = next_note;
              continue;
            }
          }
          close_glyph(
            glyphs,
            notes,
            current_glyph_type,
            current_glyph_first_note_index,
            current_note.liquescentia,
            current_note_index + 1);
          current_glyph_first_note_index = current_note_index;
          current_glyph_type = GregorioGlyphType.UNDETERMINED;
        }
        break;
      case GabcDetermination.END_OF_CURRENT:
        glyph_liquescentia += current_note.liquescentia;
        /* once again, only works with the good values in the header file */
        close_glyph(
          glyphs,
          notes,
          next_glyph_type,
          current_glyph_first_note_index,
          glyph_liquescentia,
          current_note_index);
        current_glyph_first_note_index = current_note_index + 1;
        current_glyph_type = GregorioGlyphType.UNDETERMINED;
        glyph_liquescentia = GregorioLiquescentia.NO_LIQUESCENTIA;
        break;
      case GabcDetermination.END_OF_BOTH:
        if (previous_note) {
          /* we don't want to close previous glyph twice */
          close_glyph(
            glyphs,
            notes,
            current_glyph_type,
            current_glyph_first_note_index, glyph_liquescentia,
            current_note_index - 1);
          current_glyph_first_note_index = current_note_index;
        }
        current_glyph_type = GregorioGlyphType.UNDETERMINED;
        glyph_liquescentia = GregorioLiquescentia.NO_LIQUESCENTIA;
        close_glyph(
          glyphs,
          notes,
          next_glyph_type,
          current_glyph_first_note_index,
          current_note.liquescentia,
          current_note_index);
        current_glyph_first_note_index = current_note_index + 1;
        break;
    }

    if (!next_note && current_glyph_type !== GregorioGlyphType.UNDETERMINED) {
      /* we must end the determination here */
      close_glyph(
        glyphs,
        notes,
        current_glyph_type,
        current_glyph_first_note_index,
        glyph_liquescentia,
        current_note_index);
      current_glyph_first_note_index = current_note_index;
    }

    last_pitch = current_note.pitch;

    if (last_glyph) {
      glyphs.push(last_glyph);
      last_glyph = undefined;
    }
  }
  /* end of while */

  // gregorio_go_to_first_glyph(& last_glyph);
  return glyphs;
}

function gregorio_add_clef_as_glyph(
  glyphs: GregorioGlyph[],
  clef: GregorioClefInfo,
) {
  const next_glyph = new GregorioGlyph();
  next_glyph.type = GregorioType.CLEF;
  next_glyph.misc = new GregorioMiscElementInfo();
  next_glyph.misc.clef = clef;

  glyphs.push(next_glyph);
}

function gregorio_add_pitched_element_as_glyph(
  glyphs: GregorioGlyph[],
  type: GregorioType,
  pitch: number,
  force_pitch: boolean,
) {
  // gregorio_glyph * next_glyph = create_and_link_glyph(current_glyph);

  const next_glyph = new GregorioGlyph();
  next_glyph.misc = new GregorioMiscElementInfo();

  next_glyph.type = type;
  next_glyph.misc.pitch = pitch;
  next_glyph.misc.force_pitch = force_pitch;
  // next_glyph -> texverb = texverb;

  glyphs.push(next_glyph);
}
